import React, {useEffect, useLayoutEffect, useRef} from 'react';
import {copyAllowListWithStyle, createOwnedPopoverController, getBootstrapPopoverApi, isDesktopPopoverEnabled, OwnedPopoverController} from './owned-popovers';

function createOwnedPopoverInstanceConfig(): Record<string, unknown> | undefined {
    const allowList = copyAllowListWithStyle(getBootstrapPopoverApi()?.defaultAllowList);
    return allowList ? {allowList} : undefined;
}

export function useOwnedPopovers(containerRef: React.RefObject<Element | null>, syncKey: string): void {
    const controllerRef = useRef<OwnedPopoverController | null>(null);
    if (controllerRef.current === null) {
        controllerRef.current = createOwnedPopoverController({
            popover: getBootstrapPopoverApi,
            createConfig: () => createOwnedPopoverInstanceConfig(),
        });
    }

    useLayoutEffect(() => {
        const controller = controllerRef.current;
        if (!controller) {
            return;
        }

        const sync = () => {
            controller.sync({
                container: containerRef.current,
                enabled: isDesktopPopoverEnabled(window.innerWidth),
            });
        };

        sync();
        window.addEventListener('resize', sync);
        return () => {
            window.removeEventListener('resize', sync);
        };
    }, [containerRef, syncKey]);

    useEffect(() => {
        return () => {
            controllerRef.current?.disposeAll();
        };
    }, []);
}

type OwnedPopoverScopeProps = React.HTMLAttributes<HTMLDivElement> & {
    syncKey: string;
};

export const OwnedPopoverScope: React.FC<OwnedPopoverScopeProps> = ({syncKey, children, ...domProps}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    useOwnedPopovers(containerRef, syncKey);

    return (
        <div
            ref={containerRef}
            {...domProps}
        >
            {children}
        </div>
    );
};
